import { Button, Field, HStack, Image, Input } from "@chakra-ui/react"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CounterInput from "@pathwright/ui/src/components/form/form-counter-input/CounterInput"
import FieldHeader from "@pathwright/ui/src/components/form/form-utils/FieldHeader"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Text from "@pathwright/ui/src/components/ui/Text"
import View from "@pathwright/ui/src/components/ui/View"
import { PRIMARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { useEffect, useState } from "react"
import styled from "styled-components"
import {
  MentorGroupSubscriptionFragment,
  OrderStripeCheckoutRedirects,
  OrderType
} from "@pathwright/graphql-client/src/generated"
import LazyAdaptedAuthContainer from "../auth/LazyAdaptedAuthContainer"
import OrderStripeCheckoutForm from "../order/OrderStripeCheckoutForm"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import PWAdminHint from "../pw-admin/PWAdminHint"
import StripePricePreview from "./StripePricePreview"
import { StripeProduct } from "./graphql/useStripeProducts"
import formatStripeAmount from "./utils/formatStripeAmount"
import getStartingStripePriceAmount from "./utils/getStartingStripePriceAmount"
import getStripePriceActionLabel from "./utils/getStripePriceActionLabel"
import getStripePriceIntervalLabel from "./utils/getStripePriceIntervalLabel"
import getStripePriceTotalAmount from "./utils/getStripePriceTotalAmount"
import getStripeProductMaxQuantity from "./utils/getStripeProductMaxQuantity"
import getStripeProductMinQuantity from "./utils/getStripeProductMinQuantity"

type StripeProductCardContainerProps = {
  // The stripeProduct has been flattened, making stripe_prices a simiply array (and not edges).
  stripeProduct: StripeProduct
  onSelectStripeProduct?: (stripeProduct: StripeProduct | null) => void
  onAuthStep?: (showAuthStep: boolean) => void
  inviteToken?: string
  // Handle inactive (expired) mentor group, which the user is an admin of.
  // This group has already expired, but they can resubscribe with this group
  // to reactivate it.
  reactivateMentorGroup?: MentorGroupSubscriptionFragment
  showBackButton?: boolean
  redirectTo?: string
  stripeCheckoutRedirects?: OrderStripeCheckoutRedirects
}

function SeatsField({
  minQuantity,
  maxQuantity,
  value,
  onChange,
  selectedPrice
}: {
  minQuantity: number
  maxQuantity: number
  value: number
  onChange: (quantity: number) => void
  selectedPrice: StripeProduct["stripe_prices"][number]
}) {
  const { t, tc } = useTranslate()

  return (
    <>
      <FieldHeader>
        <Text.Body>
          {tc("Enter how many seats you want to start with")}:
        </Text.Body>
      </FieldHeader>
      <StyledRow>
        <CounterInput
          min={minQuantity}
          max={maxQuantity}
          value={value}
          onChange={onChange}
          style={{ width: 80 }}
        />
        <Text.Meta>
          <StyledEmphasis>
            {formatStripeAmount(
              getStripePriceTotalAmount(selectedPrice, value),
              selectedPrice.currency
            )}
          </StyledEmphasis>
          {` ${t("per")} `}
          {getStripePriceIntervalLabel(selectedPrice, false, t)}
        </Text.Meta>
      </StyledRow>
    </>
  )
}

export default function StripeProductCard({
  stripeProduct,
  onAuthStep,
  onSelectStripeProduct,
  inviteToken,
  reactivateMentorGroup,
  showBackButton = false,
  redirectTo,
  stripeCheckoutRedirects
}: StripeProductCardContainerProps) {
  const { me } = usePathwrightContext()
  const { t, tc, tca } = useTranslate()

  const minQuantity = getStripeProductMinQuantity(stripeProduct)
  const maxQuantity = getStripeProductMaxQuantity(stripeProduct)
  const groupPricingMinQuantity = Math.max(minQuantity, 2)
  const hasIndividualPricing = minQuantity <= 1
  const hasGroupPricing = maxQuantity > 1
  // We'll only show selection UI for the individual and group options
  // if the product allows for both, otherwise, the available option
  // will automatically be selected/displayed.
  const hasIndividualAndGroupPricingOptions =
    hasIndividualPricing && hasGroupPricing
  const [showAuthStep, setShowAuthStep] = useState(false)
  const [selectedPrice, setSelectedPrice] = useState(
    stripeProduct.stripe_prices?.length === 1
      ? stripeProduct.stripe_prices[0]
      : null
  )
  const [groupPurchase, setGroupPurchase] = useState(!hasIndividualPricing)
  const [groupName, setGroupName] = useState("")
  const [unitsQuantity, setUnitsQuantity] = useState(minQuantity)
  const totalUnitsAmount = selectedPrice
    ? getStripePriceTotalAmount(selectedPrice, unitsQuantity)
    : 0
  const [hasAttemptedSubmitted, setHasAttemptedSubmitted] = useState(false)

  // Valid when not a group purchase or a group name is provided
  const isValid = !groupPurchase || !!groupName

  // If there is a mentor group, set a default units amount based on that
  // set on the group. This may not be entirely necessary. The backend overrides
  // all of these and uses the reactivation mentor group's settings instead.
  useEffect(() => {
    if (reactivateMentorGroup) {
      // Default the units quantity to the maximum number of either seats filled,
      // seats purchased (coming from seat limit), or the minimum groups quantity.
      const groupSeatsFilled = reactivateMentorGroup?.seats_filled || 0
      const groupSeatsPurchased = reactivateMentorGroup?.seat_limit || 0
      setUnitsQuantity(
        Math.max(groupSeatsFilled, groupSeatsPurchased, groupPricingMinQuantity)
      )
      setGroupName(reactivateMentorGroup.name!)
      setGroupPurchase(true)
    }
  }, [reactivateMentorGroup])

  useEffect(() => {
    onAuthStep?.(showAuthStep)
  }, [onAuthStep, showAuthStep])

  useEffect(() => {
    setShowAuthStep(false)
  }, [me])

  if (showAuthStep) {
    return (
      <CardBlock>
        {showBackButton && (
          <Button
            variant="subtle"
            colorPalette="gray"
            size="xs"
            onClick={() => {
              setShowAuthStep(false)
              onSelectStripeProduct?.(null)
            }}
          >
            <Pathicon icon="arrow-left" />
            {tca("Go Back")}
          </Button>
        )}
        <LazyAdaptedAuthContainer
          authWrapperProps={{ p: [0, 0], pt: [6, 8] }}
          inviteToken={inviteToken}
        />
      </CardBlock>
    )
  }

  return (
    <PWAdminHint model={stripeProduct}>
      <StyledCard>
        <StyledHeader>
          {!!stripeProduct.images?.[0] && (
            <Image
              src={stripeProduct.images[0]}
              alt={stripeProduct.name || "Stripe Product Image"}
            />
          )}
          <Text.H4>{stripeProduct.name}</Text.H4>
        </StyledHeader>
        <View style={{ padding: "10px 20px" }}>
          {stripeProduct.description ? (
            <Text.Body
              dangerouslySetInnerHTML={{ __html: stripeProduct.description }}
            />
          ) : null}
        </View>
        <View style={{ padding: "2px 20px 10px" }}>
          {stripeProduct.stripe_prices.map((price) => (
            <PWAdminHint model={price} key={price.id}>
              <label style={{ display: "block" }}>
                <StyledRadio>
                  <input
                    type="radio"
                    value={price.id}
                    required={true}
                    checked={
                      selectedPrice ? selectedPrice.id == price.id : false
                    }
                    onChange={() => setSelectedPrice(price)}
                    hidden={stripeProduct.stripe_prices.length === 1}
                  />
                  <div style={{ flex: 1 }}>
                    <StyledRow>
                      <Text.H5>
                        {getStripePriceActionLabel(price, t, tc)}
                      </Text.H5>
                      <Text.Meta style={{ textAlign: "right" }}>
                        {price.billing_scheme === "tiered"
                          ? `${t("starts at")} `
                          : ""}
                        <StyledEmphasis>
                          {formatStripeAmount(
                            getStartingStripePriceAmount(price),
                            price.currency
                          )}
                        </StyledEmphasis>
                        {price.recurring_interval ? (
                          <>
                            {" "}
                            {t("per")}&nbsp;
                            {getStripePriceIntervalLabel(price, false, t)}
                          </>
                        ) : (
                          ""
                        )}
                      </Text.Meta>
                    </StyledRow>
                    <Text.Meta>{price.nickname}</Text.Meta>
                  </div>
                </StyledRadio>
              </label>
            </PWAdminHint>
          ))}
        </View>
        {selectedPrice && (
          <View emphasis="secondary" style={{ padding: 20 }} roundedBottom>
            {reactivateMentorGroup ? (
              <SeatsField
                minQuantity={groupPricingMinQuantity}
                maxQuantity={maxQuantity}
                value={unitsQuantity}
                onChange={setUnitsQuantity}
                selectedPrice={selectedPrice}
              />
            ) : (
              <>
                <StyledRow>
                  {hasIndividualAndGroupPricingOptions && (
                    <Text.Body>{tc("Purchase plan for")}:</Text.Body>
                  )}
                  {selectedPrice.billing_scheme === "tiered" ? (
                    <Tooltip
                      title={<StripePricePreview stripePrice={selectedPrice} />}
                    >
                      <Text.Body>
                        <HStack gap={1}>
                          <Pathicon icon="info-circle" />
                          <span>{tca("View Pricing")}</span>
                        </HStack>
                      </Text.Body>
                    </Tooltip>
                  ) : null}
                </StyledRow>
                {hasIndividualAndGroupPricingOptions && (
                  <>
                    <label>
                      <StyledRadio>
                        <input
                          type="radio"
                          value="false"
                          checked={!groupPurchase}
                          onChange={() => {
                            setGroupPurchase(false)
                            setUnitsQuantity(1)
                          }}
                        />
                        <div>
                          <Text.H5>{tc("Just yourself")}</Text.H5>
                          <Text.Meta>
                            {tc("Get started with an individual subscription")}.
                          </Text.Meta>
                        </div>
                      </StyledRadio>
                    </label>
                    <label>
                      <StyledRadio>
                        <input
                          type="radio"
                          value="true"
                          checked={groupPurchase}
                          onChange={() => {
                            setGroupPurchase(true)
                            setUnitsQuantity(groupPricingMinQuantity)
                          }}
                        />
                        <div>
                          <Text.H5>{tc("Yourself and others")}</Text.H5>
                          <Text.Meta>
                            {tc("Learn with others on your subscription plan")}.
                          </Text.Meta>
                        </div>
                      </StyledRadio>
                    </label>
                  </>
                )}
                {groupPurchase ? (
                  <>
                    <SeatsField
                      minQuantity={groupPricingMinQuantity}
                      maxQuantity={maxQuantity}
                      value={unitsQuantity}
                      onChange={setUnitsQuantity}
                      selectedPrice={selectedPrice}
                    />
                    <>
                      <FieldHeader>
                        <Text.Body>
                          {tc(
                            `Add a name for your group that members you invite will recognize`
                          )}
                          :
                        </Text.Body>
                      </FieldHeader>
                      <Field.Root invalid={!groupName && hasAttemptedSubmitted}>
                        <Input
                          value={groupName}
                          type="text"
                          required
                          placeholder={tc("Your group name")}
                          onChange={(e) => setGroupName(e.target.value)}
                        />
                        <Field.ErrorText>
                          {t("validations.required")}
                        </Field.ErrorText>
                      </Field.Root>
                    </>
                    <br />
                  </>
                ) : null}
              </>
            )}
            {me ? (
              <OrderStripeCheckoutForm
                handleSubmitOrder={(handleOrder) => {
                  setHasAttemptedSubmitted(true)
                  if (!isValid) return
                  return handleOrder()
                }}
                submitLabel={tc(
                  reactivateMentorGroup
                    ? `Resubscribe for {{ price }} / {{ interval }}`
                    : `Subscribe for {{ price }} / {{ interval }}`,
                  {
                    price: formatStripeAmount(
                      totalUnitsAmount,
                      selectedPrice.currency
                    ),
                    interval: getStripePriceIntervalLabel(
                      selectedPrice,
                      false,
                      t
                    )
                  }
                )}
                orderType={OrderType.GroupSubscription}
                orderData={{
                  price_id: selectedPrice.id,
                  quantity: unitsQuantity || minQuantity,
                  group_name: groupName || tca("My Group"),
                  reactivate_group_subscription_id:
                    reactivateMentorGroup?.group_subscription?.id,
                  redirect_to: redirectTo
                }}
                stripeCheckoutRedirects={stripeCheckoutRedirects}
              />
            ) : (
              <CenteredContent>
                <Button
                  onClick={() => {
                    setHasAttemptedSubmitted(true)
                    if (!isValid) return
                    onSelectStripeProduct?.(stripeProduct)
                    setShowAuthStep(true)
                  }}
                  variant="solid"
                  colorPalette="brand"
                >
                  {tc("Continue")}
                </Button>
              </CenteredContent>
            )}
            <br />
            <CenteredContent>
              <Text.Meta>
                {tc("Have a coupon or gift code? Enter it at checkout")}.
              </Text.Meta>
            </CenteredContent>
          </View>
        )}
      </StyledCard>
    </PWAdminHint>
  )
}

const StyledCard = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  margin: 10px 0;
  background-color: white;
`

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  padding: 15px 20px 0;
  img {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    margin-right: 10px;
  }
`

const CenteredContent = styled.div`
  text-align: center;
`

const StyledRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`

const StyledRadio = styled.div`
  display: flex;
  align-items: baseline;
  padding: 8px 0;
  input {
    margin-right: 10px;
  }
`

const StyledEmphasis = styled.em`
  font-size: 1.2em;
  font-weight: bold;
  font-style: normal;
  color: ${PRIMARY_GRAY};
`

const StyledTooltipTrigger = styled.div`
  display: flex;
  align-items: center;
  white-space: pre-wrap;

  p {
    display: flex;
    align-items: center;
  }
`
